/**
 * SitePossibleMatchesCard.js
 *
 * Displays a list of potential matches from the database.
 * Used when a user searches for or selects an address.
 */
import React from 'react';
import PropTypes from 'prop-types';
import titleCase from '@/utils/titleCase';
import Loader from '@/components/Loader';

SitePossibleMatchesCard.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  hasMatches: PropTypes.bool.isRequired,
  sites: PropTypes.object.isRequired,
  onSelectSite: PropTypes.func.isRequired,
  onNone: PropTypes.func.isRequired,
};

/**
 * Renders a card with potential site matches and an option to use the entered address.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered matches card.
 */
function SitePossibleMatchesCard(props) {
  const { isFetching, hasMatches, sites, onSelectSite, onNone } = props;
  if (isFetching) return (<Loader />)

  return (
    <div className="bg-white border border-gray-300 rounded-[10px] shadow-md pt-4 pb-8 px-4 flex flex-col gap-2">
      <div className="pb-3">
        <p className="font-semibold text-sm text-gray-900">
          Is this your school or organization?
        </p>
      </div>

      {hasMatches && (
        <>
          {sites.data.map((site, idx) => (
            <React.Fragment key={site.id}>
              <div
                className="flex cursor-pointer hover:opacity-75"
                onClick={() => onSelectSite(site)}
              >
                <div className="flex flex-col gap-1">
                  <p className="text-base text-gray-600 underline">{site.data.name}</p>
                  <div className="flex flex-col gap-2 text-2m text-gray-700">
                    <p>
                      <span className="font-bold">{titleCase(site.data.addressLine1 || '')}</span>
                      {site.data.city && `, ${titleCase(site.data.city)}`}
                      {site.data.state && `, ${site.data.state}`}
                    </p>
                  </div>
                </div>
              </div>
              {idx < sites.data.length - 1 && (
                <div className="py-1">
                  <div className="h-px bg-gray-300 w-full" />
                </div>
              )}
            </React.Fragment>
          ))}
          <div className="pt-2">
            <div className="h-px bg-gray-300 w-full" />
          </div>
        </>
      )}

      <button
        type="button"
        className="text-sm text-cs-blue-500 underline text-left w-fit pt-2"
        onClick={onNone}
      >
        Use the address I entered
      </button>
    </div>
  );
}

export default SitePossibleMatchesCard;
